import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { saveArtistInterview } from '../redux/ArtistInterviewSlice';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ImageUpload from './ImageUpload';
import { getArtistInterviews } from '../redux/ArtistInterviewSlice';

const ArtistInterviewModal = ({ isOpen, onClose, interviewData, onSuccess }) => {
    const dispatch = useDispatch();
    const { register, handleSubmit, setValue, setError, clearErrors, control, formState: { errors } } = useForm();
    const [editorValue, setEditorValue] = useState('');
    const [gallery, setGallery] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const categoryOptions = ['Artists Interviews', 'Artists Guides'];

    useEffect(() => {
        if (interviewData) {
            setValue('interview_title', interviewData.interview_title);
            setValue('interview_category', interviewData.interview_category);
            setValue('interview_artistic_fields', interviewData.interview_artistic_fields);
            setValue('artist_title', interviewData.artist_title);
            setValue('artist_profile_url', interviewData.artist_profile_url);
            setValue('artist_website', interviewData.artist_website);
            setValue('artist_instagram', interviewData.artist_instagram);
            setEditorValue(interviewData.interview_content);
            setGallery(interviewData.gallery || []);
        } else {
            setEditorValue('');
            setGallery([]);
        }
    }, [interviewData, setValue]);

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        
        // Validate editor content
        if (!editorValue || editorValue.trim() === '<p><br></p>' || editorValue.trim() === '') {
            toast.error('Interview content is required');
            setIsSubmitting(false);
            return;
        }
        
        const interview = { ...data, interview_content: editorValue, gallery };

        if (interviewData?.id) {
            interview.id = interviewData.id;
        }

        try {
            await dispatch(saveArtistInterview(interview)).unwrap();
            const message = interviewData?.id ? 'Interview updated successfully!' : 'Interview created successfully!';
            toast.success(message);
            // 🔄 Refresh the artist interviews list after save
            dispatch(getArtistInterviews());
            onSuccess(message);
            onClose();
        } catch (err) {
            // Show specific error message from API response
            const errorMessage = err?.payload?.message || 
                               err?.response?.data?.message || 
                               err?.message || 
                               (interviewData?.id ? 'Failed to update interview' : 'Failed to create interview');
            toast.error(errorMessage);
            console.error('Interview save error:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditorChange = (value) => {
        setEditorValue(value);
    };

    if (!isOpen) return null; // Prevent rendering when modal is closed

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-2xl p-6 rounded-lg shadow-lg relative max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <h2 className="text-2xl mb-4">{interviewData ? 'Edit Interview' : 'Create Interview'}</h2>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium">Interview Title <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            {...register('interview_title', { required: 'Title is required' })}
                            className="w-full p-2 border rounded"
                        />
                        {errors.interview_title && <p className="text-red-500 text-sm">{errors.interview_title.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium">Interview Category <span className="text-red-500">*</span></label>
                        <select
                            {...register('interview_category', { required: 'Category is required' })}
                            className="w-full p-2 border rounded"
                            defaultValue={interviewData?.interview_category || 'Artists Guides'}
                        >
                            {categoryOptions.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium">Artistic Fields <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            {...register('interview_artistic_fields', { required: 'Artistic fields are required' })}
                            className="w-full p-2 border rounded"
                        />
                        {errors.interview_artistic_fields && <p className="text-red-500 text-sm">{errors.interview_artistic_fields.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium">Artist Title <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            {...register('artist_title', { required: 'Artist title is required' })}
                            className="w-full p-2 border rounded"
                        />
                        {errors.artist_title && <p className="text-red-500 text-sm">{errors.artist_title.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium">Artist Profile URL <span className="text-red-500">*</span></label>
                        <input
                            type="url"
                            {...register('artist_profile_url', { required: 'Profile URL is required' })}
                            className="w-full p-2 border rounded"
                        />
                        {errors.artist_profile_url && <p className="text-red-500 text-sm">{errors.artist_profile_url.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium">Artist Website <span className="text-red-500">*</span></label>
                        <input
                            type="url"
                            {...register('artist_website', { required: 'Website is required' })}
                            className="w-full p-2 border rounded"
                        />
                        {errors.artist_website && <p className="text-red-500 text-sm">{errors.artist_website.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium">Artist Instagram <span className="text-red-500">*</span></label>
                        <input
                            type="url"
                            {...register('artist_instagram', { required: 'Instagram URL is required' })}
                            className="w-full p-2 border rounded"
                        />
                        {errors.artist_instagram && <p className="text-red-500 text-sm">{errors.artist_instagram.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium">Interview Content <span className="text-red-500">*</span></label>
                        <ReactQuill
                            value={editorValue}
                            onChange={handleEditorChange}
                            theme="snow"
                            modules={{
                                toolbar: [
                                    [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
                                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                    ['bold', 'italic', 'underline'],
                                    [{ 'align': [] }],
                                    ['link', 'image'],
                                    [{ 'color': [] }, { 'background': [] }],
                                    [{ 'script': 'sub' }, { 'script': 'super' }],
                                    ['blockquote', 'code-block'],
                                    [{ 'direction': 'rtl' }],
                                ],
                            }}
                            className="w-full border rounded"
                        />
                        {(!editorValue || editorValue.trim() === '<p><br></p>' || editorValue.trim() === '') && (
                            <p className="text-red-500 text-sm mt-1">Interview content is required</p>
                        )}
                    </div>

                    {/* Gallery Upload */}
                    <div>
                        <label className="block text-sm font-medium">Gallery</label>
                        {console.log('gallery', gallery)}
                        <Controller
                            name="gallery"
                            control={control}
                            render={({ field }) => (
                                <ImageUpload
                                    label="Artist's Gallery"
                                    initialImages={gallery}
                                    onImageUpload={(images) => {
                                        setGallery(images);
                                        setValue('gallery', images);
                                        clearErrors('gallery');
                                    }}
                                    onError={(message) => setError('gallery', { type: 'manual', message })}
                                />
                            )}
                        />
                        {errors.gallery && <p className="text-red-500 text-sm">{errors.gallery.message}</p>}
                    </div>
                    <div className="flex justify-end space-x-4">
                        <button type="button" onClick={onClose} className="bg-gray-500 text-white px-4 py-2 rounded" disabled={isSubmitting}>Cancel</button>
                        <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded disabled:bg-gray-400" disabled={isSubmitting}>
                            {isSubmitting ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ArtistInterviewModal;